// The console controller: what the zones (workspace, context, action rail, drawers) can read and do.
// ConsoleScreen builds it once per render and passes it down.

import type { AgentPanel, EventCamera, EventDetail, FeaturesConfig, MissionStep, StepStatus, Watch } from '../api.ts';
import type { PendingApproval } from '../live/copilot.tsx';
import type { RoutingConfig } from '../auth.ts';
import type { DevicesState } from './devices.ts';
import type { DraftStore } from './drafts.ts';
import type { Ptt } from './ptt.ts';
import type { ToastSpec } from './Toasts.tsx';
import type { PlayMode, View } from './view.ts';

export type CtxTab = 'agent' | 'mission' | 'map' | 'contacts' | 'intel' | 'log';
/** 'agent' shows only with features.agent (Phase 3). */
export const CTX_TABS: readonly CtxTab[] = ['agent', 'mission', 'map', 'contacts', 'intel', 'log'];

/** The agent panel for the open event (GET /api/events/:id/agent), refetched on its SSE `agent` and `event` messages. */
export interface AgentState {
  /** features.agent */
  on: boolean;
  /** undefined while loading; null when the event has no agent run (404). */
  data: AgentPanel | null | undefined;
  error: unknown;
  reload: () => void;
}

export interface DataIssuePrefill {
  entityType?: string;
  entityId?: string;
  subItem?: { kind: 'phone' | 'email' | 'zone'; id: string };
  field?: string;
  issueType?: string;
  severity?: string;
  description?: string;
  suggestedValue?: string;
}

export type DrawerSpec =
  | { k: 'dispatch' }
  | { k: 'disposition' }
  | { k: 'note' }
  | { k: 'park' }
  | { k: 'transfer' }
  | { k: 'dataIssue'; prefill: DataIssuePrefill }
  /* Phase 5 (features.deterrence): talk-down (T), deterrents (R), notify the customer */
  | { k: 'talkdown' }
  | { k: 'deter'; relayId?: string }
  | { k: 'notify' };

export interface Ctl {
  ev: EventDetail;
  meId: string;
  /** I own this event and it is active: commands are allowed. */
  canAct: boolean;
  features: FeaturesConfig;
  routing: RoutingConfig;
  /** Supervisor or admin (Reassign in Send to). */
  isSup: boolean;
  maxActive: number;
  activeCount: number;
  view: View;
  /** The site's cameras by distance rank (keys 1–9). The rail is gone: all cameras are in the pop-out matrix. */
  railCams: EventCamera[];
  /** Alarm ids that arrived live while the event was open. */
  newIds: ReadonlySet<string>;
  /** The last command hit a 409: the event was refetched ("Event changed, review and retry"). */
  conflict: boolean;
  /** Label of the command in flight, if any. */
  busy: string | null;

  /**
   * Runs an event command with the current version; handles 409 (refetch + banner) and other errors (toast)
   * unless `onError` returns true. `apply` picks the EventDetail to show from the result.
   */
  run: <T>(
    label: string,
    fn: (expectedVersion: number) => Promise<T>,
    apply?: (r: T) => EventDetail | null,
    onError?: (e: unknown) => boolean,
  ) => Promise<T | undefined>;
  reload: () => void;
  /** After a successful disposition: back to the queue. */
  afterClose: (d: EventDetail) => void;
  /** The event left my tabs (parked, sent, reassigned): show the next tab. */
  afterLeave: (eventId: string) => void;
  /** Drawer drafts, kept per event while switching tabs. */
  drafts: DraftStore;
  toast: (t: ToastSpec) => void;

  selectAlarm: (id: string) => void;
  /** J: the next unreviewed alarm after the selected one (in time order, wrapping). */
  nextNew: () => void;
  /** M: opens (or focuses) the camera matrix window for this event. */
  openMatrix: () => void;
  selectCamera: (id: string) => void;
  setMode: (m: PlayMode) => void;
  /** Toggles sync; `true` always turns it on (mission step button). */
  toggleSync: (on?: boolean) => void;
  setLayout: (l: 'single' | 'quad') => void;
  playPause: () => void;
  jump: (seconds: number) => void;
  seekTo: (t: number) => void;
  /** Opens fresh video sessions at the current playhead (after the page was hidden). */
  renewVideo: () => void;
  /** The main recorded stream ended at this time: carry on (view.ts continueAfter). */
  videoEnded: (endedAt: number) => void;
  bookmark: () => void;
  setStep: (step: MissionStep, status: StepStatus) => void;

  /** Phase 3: the AI agent's run on this event. */
  agent: AgentState;
  /** Phase 4: the event's AI watches (GET /api/events/:id/watches); undefined while loading. */
  watches: Watch[] | undefined;
  reloadWatches: () => void;
  /** Phase 4: the agent's pending requests on this event, oldest first (Y / N answer the newest). */
  approvals: PendingApproval[];
  /** Opens the Agent tab and focuses the command box (G). Bumps cmdFocus. */
  askAgent: () => void;
  cmdFocus: number;

  /** Phase 5: the site's speakers and relays with live state (features.deterrence). */
  devices: DevicesState;
  /** Phase 5: the speakers a talk-down or push-to-talk goes to (the operator's pick, else the nearest). */
  speakers: string[];
  setSpeakers: (ids: string[]) => void;
  /** Phase 5: push-to-talk (hold T, or the drawer's button). */
  ptt: Ptt;
  /** Starts push-to-talk on the chosen speakers (nothing for read-only viewers). */
  pttPress: () => void;
  /** Opens the pop-out video window (the main canvas, time-synced). */
  openVideo: () => void;

  openDrawer: (d: DrawerSpec) => void;
  ctx: CtxTab;
  setCtx: (t: CtxTab) => void;
}
